"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.calculateOnCallTimeBreakdown = calculateOnCallTimeBreakdown;
const luxon_1 = require("luxon");
/**
 * Calculates the breakdown of ON_CALL vs CALLED_IN time periods for an on-call shift
 * with multiple clock entries.
 *
 * On-call gaps (before/between/after called-in segments) are clamped to the
 * `[onCallStart, onCallEnd]` window. Called-in segments are recorded at their
 * full duration even if they extend past `onCallEnd`.
 *
 * @param onCallStart - When the on-call period actually started
 * @param onCallEnd - When the on-call period actually ended
 * @param clockEntries - Array of called in clock in/out entries with meal break information
 * @param mealBreakMinutes - Duration of meal break in minutes (default: 30)
 * @returns Breakdown of time events and aggregated totals
 */
function calculateOnCallTimeBreakdown(onCallStart, onCallEnd, clockEntries, mealBreakMinutes = 30) {
    const events = [];
    const onCallStartDt = luxon_1.DateTime.fromJSDate(onCallStart);
    const onCallEndDt = luxon_1.DateTime.fromJSDate(onCallEnd);
    const pushOnCallGap = (gapStart, gapEnd) => {
        const start = gapStart < onCallStartDt ? onCallStartDt : gapStart;
        const end = gapEnd > onCallEndDt ? onCallEndDt : gapEnd;
        const minutes = Math.round(end.diff(start, 'minutes').minutes);
        if (minutes <= 0) {
            return;
        }
        events.push({
            type: 'ON_CALL',
            startTime: start.toJSDate(),
            endTime: end.toJSDate(),
            minutesWorked: minutes,
            mealBreakMinutes: 0,
        });
    };
    if (!clockEntries || clockEntries.length === 0) {
        // No called-in segments: the entire on-call window is ON_CALL.
        pushOnCallGap(onCallStartDt, onCallEndDt);
        return {
            events,
            totalOnCallMinutes: events[0]?.minutesWorked ?? 0,
            totalCalledInMinutes: 0,
            totalMealBreakMinutes: 0,
        };
    }
    const sortedEntries = [...clockEntries].sort((a, b) => a.clockInTime.getTime() - b.clockInTime.getTime());
    // Add initial ON_CALL period: on-call start → first clock in
    pushOnCallGap(onCallStartDt, luxon_1.DateTime.fromJSDate(sortedEntries[0].clockInTime));
    // Process each clock entry and gaps between them
    for (let i = 0; i < sortedEntries.length; i++) {
        const entry = sortedEntries[i];
        const clockInDt = luxon_1.DateTime.fromJSDate(entry.clockInTime);
        const clockOutDt = luxon_1.DateTime.fromJSDate(entry.clockOutTime);
        const calledInMinutes = Math.round(clockOutDt.diff(clockInDt, 'minutes').minutes);
        const entryMealBreakMinutes = entry.tookMealBreak
            ? mealBreakMinutes
            : 0;
        // Add CALLED_IN event (full duration, even if it extends past onCallEnd)
        events.push({
            type: 'CALLED_IN',
            startTime: entry.clockInTime,
            endTime: entry.clockOutTime,
            minutesWorked: calledInMinutes - entryMealBreakMinutes,
            mealBreakMinutes: entryMealBreakMinutes,
        });
        // Add ON_CALL period between this and next entry (if exists)
        if (i < sortedEntries.length - 1) {
            const nextEntry = sortedEntries[i + 1];
            pushOnCallGap(clockOutDt, luxon_1.DateTime.fromJSDate(nextEntry.clockInTime));
        }
    }
    // Add final ON_CALL period: last clock out → on-call end
    const lastEntry = sortedEntries[sortedEntries.length - 1];
    pushOnCallGap(luxon_1.DateTime.fromJSDate(lastEntry.clockOutTime), onCallEndDt);
    const totalOnCallMinutes = events
        .filter((e) => e.type === 'ON_CALL')
        .reduce((sum, e) => sum + e.minutesWorked, 0);
    const totalCalledInMinutes = events
        .filter((e) => e.type === 'CALLED_IN')
        .reduce((sum, e) => sum + e.minutesWorked, 0);
    const totalMealBreakMinutes = events.reduce((sum, e) => sum + e.mealBreakMinutes, 0);
    return {
        events,
        totalOnCallMinutes,
        totalCalledInMinutes,
        totalMealBreakMinutes,
    };
}
//# sourceMappingURL=on-call-time-calculation.js.map